import { useCallback, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@material-ui/core';
import { InfoCard, Link, Progress, StatusError, StatusOK, StatusPending, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { Entity, RELATION_HAS_PART, stringifyEntityRef } from '@backstage/catalog-model';

const GITHUB = 'https://api.github.com';
const CD_WORKFLOW = 'github-action-cd.yml';
const TAG = /^(av|bv|v)\d/;

export type Pending = { slug: string; tag: string; env: string; runId: number; url: string; envIds: number[]; canApprove: boolean; since: string };
type Result = { ok: boolean; error?: string };

// Mismo criterio que el CD: v* = prod, bv* = qa, av* = dev.
export const envOf = (tag: string) => {
  if (tag.startsWith('v')) return 'prod';
  return tag.startsWith('bv') ? 'qa' : 'dev';
};

/** CD de la iniciativa que esperan aprobación en ese ambiente y que GitHub deja aprobar a este usuario. */
export const approvable = (rows: Pending[], env: string) => rows.filter(r => r.env === env && r.canApprove);

const Initiative = ({ entity }: { entity: Entity }) => {
  const catalog = useApi(catalogApiRef);
  const githubAuth = useApi(githubAuthApiRef);
  const discovery = useApi(discoveryApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);
  const [rows, setRows] = useState<Pending[]>();
  const [error, setError] = useState<string>();
  const [env, setEnv] = useState('prod');
  const [confirm, setConfirm] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<Record<number, Result>>({});

  const gh = useCallback(
    async (path: string, init?: RequestInit) => {
      const token = await githubAuth.getAccessToken(['repo']);
      const res = await fetch(`${GITHUB}${path}`, {
        ...init,
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/vnd.github+json', 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`GitHub respondió ${res.status}${res.status === 404 ? '' : `: ${(await res.text()).slice(0, 200)}`}`);
      return res.status === 204 ? undefined : res.json();
    },
    [githubAuth],
  );

  const load = useCallback(async () => {
    const refs = (entity.relations ?? []).filter(r => r.type === RELATION_HAS_PART && r.targetRef.startsWith('component:')).map(r => r.targetRef);
    const { items } = await catalog.getEntitiesByRefs({ entityRefs: refs });
    const slugs = items.map(e => e?.metadata.annotations?.['github.com/project-slug']).filter((s): s is string => !!s);
    const found = await Promise.all(
      slugs.map(async slug => {
        const { workflow_runs: runs } = await gh(`/repos/${slug}/actions/workflows/${CD_WORKFLOW}/runs?status=waiting&per_page=20`).catch(() => ({ workflow_runs: [] }));
        return Promise.all(
          runs
            .filter((run: any) => TAG.test(run.head_branch ?? ''))
            .map(async (run: any): Promise<Pending> => {
              const pending: any[] = await gh(`/repos/${slug}/actions/runs/${run.id}/pending_deployments`);
              return {
                slug,
                tag: run.head_branch,
                env: envOf(run.head_branch),
                runId: run.id,
                url: run.html_url,
                envIds: pending.map(p => p.environment.id),
                canApprove: pending.length > 0 && pending.every(p => p.current_user_can_approve),
                since: run.run_started_at,
              };
            }),
        );
      }),
    );
    setRows(found.flat().sort((a, b) => a.slug.localeCompare(b.slug)));
  }, [catalog, entity, gh]);

  useEffect(() => {
    load().catch(e => setError(String(e?.message ?? e)));
  }, [load]);

  const approveAll = async () => {
    setConfirm(false);
    setRunning(true);
    const targets = approvable(rows ?? [], env);
    const name = entity.metadata.title ?? entity.metadata.name;
    const outcome: Record<number, Result> = {};
    await Promise.all(
      targets.map(async r => {
        try {
          await gh(`/repos/${r.slug}/actions/runs/${r.runId}/pending_deployments`, {
            method: 'POST',
            body: JSON.stringify({ environment_ids: r.envIds, state: 'approved', comment: `Aprobación en lote desde Backstage: iniciativa ${name}` }),
          });
          outcome[r.runId] = { ok: true };
        } catch (e: any) {
          outcome[r.runId] = { ok: false, error: String(e?.message ?? e) };
        }
      }),
    );
    setResults(outcome);
    await backstageFetch(`${await discovery.getBaseUrl('releases')}/batch-approvals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        initiative: stringifyEntityRef(entity),
        items: targets.map(r => ({ slug: r.slug, tag: r.tag, env: r.env, url: r.url, ok: outcome[r.runId].ok })),
      }),
    }).catch(() => undefined);
    setRunning(false);
  };

  const columns: TableColumn<Pending>[] = [
    { title: 'Servicio', field: 'slug', render: r => r.slug.split('/')[1] },
    { title: 'Versión', field: 'tag', render: r => <Link to={r.url}>{r.tag}</Link> },
    { title: 'Ambiente', field: 'env' },
    { title: 'Esperando desde', field: 'since', render: r => new Date(r.since).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' }) },
    { title: 'Puedes aprobar', render: r => (r.canApprove ? 'Sí' : 'No (no eres aprobador)') },
    {
      title: 'Resultado',
      render: r => {
        const res = results[r.runId];
        if (!res) return '';
        return res.ok ? <StatusOK>Aprobado: desplegando</StatusOK> : <StatusError>{res.error}</StatusError>;
      },
    },
  ];
  const targets = approvable(rows ?? [], env);

  return (
    <InfoCard title="Desplegar la iniciativa" subheader="CD de sus MSA que esperan aprobación; se aprueban juntos con tu usuario de GitHub">
      {error && <WarningPanel title="No se pudieron leer los despliegues" message={error} />}
      {!rows && !error && <Progress />}
      {rows && (
        <>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12 }}>
            <TextField select size="small" label="Ambiente" value={env} onChange={e => setEnv(e.target.value)} style={{ minWidth: 120 }}>
              {['prod', 'qa', 'dev'].map(e => (
                <MenuItem key={e} value={e}>
                  {e}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" color="primary" disabled={!targets.length || running} onClick={() => setConfirm(true)}>
              Aprobar {targets.length} despliegue(s)
            </Button>
            {running && <StatusPending>Aprobando…</StatusPending>}
            <Button size="small" onClick={() => load().catch(e => setError(String(e?.message ?? e)))}>
              Actualizar
            </Button>
          </div>
          <Table<Pending>
            options={{ paging: false, search: false, padding: 'dense', toolbar: false }}
            columns={columns}
            data={rows.filter(r => r.env === env)}
            emptyContent={<div style={{ padding: 16 }}>Ningún MSA de la iniciativa espera aprobación en {env}.</div>}
          />
          <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 8 }}>
            Se despliegan en paralelo, sin orden. Si uno falla, los demás siguen: revísalo en Releases y usa su rollback si hace falta.
          </Typography>
        </>
      )}
      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>Aprobar {targets.length} despliegue(s) en {env}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{targets.map(t => `${t.slug.split('/')[1]} ${t.tag}`).join(' · ')}</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Queda registrado en GitHub con tu usuario y en Backstage como aprobación en lote.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={approveAll}>
            Aprobar
          </Button>
        </DialogActions>
      </Dialog>
    </InfoCard>
  );
};

export const isInitiative = (entity: Entity) => entity.spec?.type === 'initiative';

/** Solo en Systems de tipo initiative (MSA enlazados con deuna.com/initiative-members). */
export const InitiativeDeployCard = () => {
  const { entity } = useEntity();
  return isInitiative(entity) ? <Initiative entity={entity} /> : null;
};
